import MuiDrawer from '@mui/material/Drawer';
import { styled as muiStyled, css, useTheme } from '@mui/material/styles';
import React from 'react';

import Arranger from '../arranger';
import Button from '../button';
import Text from '../text';

interface Props extends React.PropsWithChildren {
  className?: string;
  /**
   * Controls if the drawer is open
   */
  open: boolean;
  /**
   * Callback fired when the drawer requests to be closed
   */
  onClose: () => void;
  /**
   * The width of the drawer when opened. A number is treated as pixels; a string
   * is used as-is (e.g. '50vw').
   * @default 480
   */
  width?: number | string;
  /**
   * Side from which the drawer will appear
   * @default 'right'
   */
  anchor?: 'left' | 'right' | 'top' | 'bottom';
  /**
   * Title of the drawer
   */
  title?: string;
  actionButtons?: React.ReactNode;
  showCloseButton?: boolean;
}

const DrawerImpl: React.FC<Props> = ({
  className,
  open,
  onClose,
  width = 480,
  anchor = 'right',
  children,
  title,
  actionButtons,
  showCloseButton = true,
}) => {
  const theme = useTheme();
  return (
    <MuiDrawer
      className={className}
      open={open}
      onClose={onClose}
      anchor={anchor}
      transitionDuration={500}
      sx={{
        '& .MuiDrawer-paper': {
          // Full width on phones; the configured width from the `sm` breakpoint up.
          width: {
            xs: '100vw',
            sm: typeof width === 'number' ? `${width}px` : width,
          },
          maxWidth: '100vw',
          boxSizing: 'border-box',
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
      }}
    >
      <div className="drawer-container">
        {(title || actionButtons || showCloseButton) && (
          <div className="drawer-header">
            <Arranger
              direction="row"
              alignItems="center"
              justify="space-between"
              sx={{ width: '100%' }}
            >
              {title && (
                <Text
                  sz="h2"
                  className="drawer-title"
                  style={{ fontWeight: 600 }}
                >
                  {title}
                </Text>
              )}
              <Arranger
                className="action-buttons"
                direction="row"
                alignItems="center"
                sx={{ gap: 1, marginLeft: 'auto' }}
              >
                {actionButtons}
                {showCloseButton && (
                  <Button
                    onClick={onClose}
                    size="small"
                    color="secondary"
                    outlined
                  >
                    Close
                  </Button>
                )}
              </Arranger>
            </Arranger>
          </div>
        )}
        <div className="drawer-content">{children}</div>
      </div>
    </MuiDrawer>
  );
};

/**
 * Base Drawer Component
 */
export const Drawer = muiStyled(DrawerImpl)(
  ({ theme }) => css`
    .MuiDrawer-paper {
      flex-shrink: 0;
      white-space: pre-wrap;
      box-sizing: border-box;
      overflow-x: hidden;
      background-color: ${theme.palette.background.paper};
      color: ${theme.palette.text.primary};
    }

    .drawer-container {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .drawer-header {
      padding: ${theme.spacing(3)} ${theme.spacing(4)};
      border-bottom: 1px solid ${theme.palette.divider};
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .drawer-title {
      margin: 0;
    }

    .drawer-content {
      flex: 1;
      overflow-y: auto;
      padding: ${theme.spacing(3)} ${theme.spacing(4)};
    }

    .action-buttons {
      .MuiButton-root {
        border-radius: ${theme.spacing(1)};
      }
    }
  `
);
